import { CategoryChartQueryFn, CategoryChartQueryParam } from "../categoryChart";
import {
    deltaValue,
    getBaseValue,
    KpiReport,
    // getKpiReportFromGQResult, isUnitDollar, KpiReport, KpiReportEntity,
    roundValue,
    Unit
} from "../processors";
// import { queryEntities } from "../misc";
import { FigureSolidDashKpiProvider } from "./categoryChartData.types";
import _ from "lodash";
import { getKpisForProvidersWithCategoryChart } from "../../Components/opendata";
import { figureDeltaString } from "./figureDeltaString";
import { appMedian, extractInitialYearWith1DimValues, VALUE_STATUS_NAMING } from "../misc";
import { getConnectionSizeMedianText } from "../../utils";

export const fetchFigureSolidDashKpiProviderChartData: CategoryChartQueryFn = async ({
    metadata,
    categoryChart,
    providers,
    kpis,
    category
}: CategoryChartQueryParam): Promise<FigureSolidDashKpiProvider> => {
    const provider = providers?.[0];
    const kpi = kpis?.[0];
    if (!metadata || !provider || !categoryChart || kpi == null) return null;

    let kpiReportResults = await getKpisForProvidersWithCategoryChart([kpi], [provider], true, categoryChart);

    if (!kpiReportResults) return null;

    kpiReportResults = (kpiReportResults ?? []).filter(d => d != null && d.provider != null);

    // group by year
    const groupByYearKpiReportResults = _.groupBy(kpiReportResults, d => d.year);

    // kpi report results for the provider
    const providerKpiReportResults = _.uniqBy(
        kpiReportResults.filter(d => d?.provider?.tid === provider?.tid),
        d => d.year
    );

    if (!providerKpiReportResults || !providerKpiReportResults?.length) return null;

    const { displayTitle, visType, figureHeader, figureFooter, visHeader, visFooter } = categoryChart;

    // find unit, last year and previous year data
    let unit = _.uniqBy(providerKpiReportResults, d => d.unit ?? "")?.[0]?.unit ?? "";
    const yearToProviderKpiReportResults = _.keyBy(providerKpiReportResults, d => d.year);

    const years = _.orderBy(_.uniq(kpiReportResults?.map(d => d.year) ?? []), d => d, "asc") ?? [];
    const latestYear = years?.[years?.length - 1];
    const previousYear = years?.[years?.length - 2];

    if (latestYear == null) return null;

    const latestYearData = yearToProviderKpiReportResults?.[latestYear];
    const previousYearData = yearToProviderKpiReportResults?.[previousYear];

    const {
        value: latestYearValue,
        valueStatus: latestYearValueStatus,
        unit: latestBaseUnit
    } = !latestYearData
        ? ({} as KpiReport)
        : {
              ...latestYearData,
              ...getBaseValue(latestYearData?.value, latestYearData?.unit)
          };

    const { value: previousYearValue, unit: previousBaseUnit } = !previousYearData
        ? ({} as KpiReport)
        : {
              ...previousYearData,
              ...getBaseValue(previousYearData?.value, previousYearData?.unit)
          };

    unit = latestBaseUnit ?? previousBaseUnit ?? unit;

    // find delta of values between latest year and previous year
    const delta = deltaValue(previousYearData?.value, latestYearValue, unit as Unit);
    const deltaString = figureDeltaString(delta, category);

    // const fawStyle = `style="color: ${getColor(category?.theme)}"`;
    // const deltaString = `${
    //     delta === 0
    //         ? `<span class='fa-solid fa-right mr-1' ${fawStyle}></span> No change from last year`
    //         : `${
    //               delta > 0
    //                   ? `<span class='fa-solid fa-up mr-1' ${fawStyle}></span>`
    //                   : delta < 0
    //                   ? `<span class='fa-solid fa-down mr-1' ${fawStyle}></span>`
    //                   : ""
    //           } ${delta}% ${delta > 0 ? "above" : "below"} last year`
    // }`;

    // create content for header and footer of the figure
    const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
        year: latestYear,
        value: latestYearValue,
        deltaString
    });

    const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
        year: latestYear,
        value: latestYearValue,
        deltaString
    });

    // create chart data
    const measure = kpi?.[2] ?? latestYearData?.[0]?.indicatorTitle ?? "Line data";
    const lineDataSeriesName = provider?.name;

    const providerMedianSeriesName = getConnectionSizeMedianText(metadata, provider?.connectionSizeCategory);

    const values = years.map(year => {
        const yearBasedReports = groupByYearKpiReportResults?.[year] ?? [];
        const providerValue = yearBasedReports.find(d => d?.provider?.tid === provider?.tid);

        const baseValue = getBaseValue(providerValue?.value, providerValue?.unit);
        if (baseValue?.value != null) unit = baseValue?.unit;

        return baseValue?.value == null ? null : roundValue(baseValue?.value, unit as Unit, true, false)?.value;

        // return providerValue?.value == null ? null : roundValue(providerValue?.value, unit as Unit).value;
    });

    const averages = years.map(year => {
        const yearBasedReports = groupByYearKpiReportResults?.[year] ?? [];
        const sameConCategoryProviders = yearBasedReports.filter(
            d =>
                d.value != null &&
                d?.provider?.connectionSizeCategory ===
                    // "large"
                    provider?.connectionSizeCategory
        );

        const values = sameConCategoryProviders?.map(d => getBaseValue(d?.value, d?.unit));

        if (values?.find(d => d?.value != null)) unit = values?.find(d => d?.value != null)?.unit;

        return appMedian(
            values.map(d => d?.value),
            unit as Unit
        );
        // const values = large?.filter(d => d.value != null)?.map(d => d.value);
        // if (!values?.length) return null;

        // // const average = median(large?.filter(d=>d.value != null)?.map(d=>d.value));
        // const average = median(values);
        // return roundValue(average ?? 0, unit as Unit).value;

        // const average = _.meanBy(large, d => d.value ?? 0);
        // return roundValue(average ?? 0, unit as Unit).value;
    });

    const initialYear = extractInitialYearWith1DimValues(years, values, averages);

    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear: initialYear,
        endYear: latestYear
    });

    const ret: FigureSolidDashKpiProvider = {
        unit: unit as Unit,
        year: latestYear,
        waterProvider: provider,
        kpiCode: kpi[0],
        schemeType: kpi[1],
        title: displayTitle,
        footer: null,
        figure: !(visType === "figure" || visType === "figureWithSolidDashLine")
            ? null
            : {
                  decimals:
                      latestYearValue != null ? roundValue(latestYearValue, unit as Unit, true, false).decimals : 0,
                  header:
                      !figureHeader || figureHeader.processed.trim() === ""
                          ? null
                          : {
                                processed: figureHeaderProcessed,
                                value: figureHeaderProcessed,
                                format: figureHeaderProcessed
                            },
                  value:
                      latestYearValueStatus != null
                          ? VALUE_STATUS_NAMING?.[latestYearValueStatus] ?? latestYearValueStatus
                          : previousYearValue != null && latestYearValue == null
                          ? VALUE_STATUS_NAMING.MD
                          : latestYearValue == null
                          ? VALUE_STATUS_NAMING.NR
                          : roundValue(latestYearValue, unit as Unit, true, false).value,

                  footer:
                      !figureFooter || figureFooter.processed.trim() === ""
                          ? null
                          : {
                                processed: figureFooterProcessed,
                                value: figureFooterProcessed,
                                format: figureFooterProcessed
                            }
              },
        graph:
            visType === "figure"
                ? null
                : {
                      years,
                      measure,
                      lineData: values ?? [],
                      providerMedianData: averages ?? [],
                      providerMedianSeriesName,
                      providerMedianAnnotation: providerMedianSeriesName,

                      // largeProviderAverageSeriesName: `${largeProviderAverageSeriesName}`,
                      // largeProviderAverageData: averages ?? [],
                      lineDataSeriesName: `${lineDataSeriesName}`,
                      header:
                          !visHeader || visHeader.processed.trim() === ""
                              ? null
                              : {
                                    processed: visHeaderProcessed,
                                    value: visHeaderProcessed,
                                    format: visHeaderProcessed
                                },
                      footer:
                          !visFooter || visFooter.processed.trim() === ""
                              ? null
                              : {
                                    processed: visFooterProcessed,
                                    value: visFooterProcessed,
                                    format: visFooterProcessed
                                }
                  }
    };

    return ret;
};
